import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./DocumentViewer.css";
import { logInteraction } from "../utils/logInteraction";
import Select from "react-select";
import PreviewModal from "./PreviewModal";
import MetadataModal from "./MetadataModal";
import Fuse from "fuse.js";
import { useLocation } from "react-router-dom";

function DocumentViewer({ user }) {
    const roleName = String(user?.role || "");
    const canManageAccess = roleName === "Admin" || roleName === "Archivist";
    const canEdit = roleName === "Admin" || roleName === "Archivist";
    const canDelete = roleName === "Admin" || roleName === "Archivist";
    const canReviewDescriptions = roleName === "Archivist";

    // -------- Analytics --------
    useEffect(() => {
        logInteraction(
            "page_view",
            { viewport: { width: window.innerWidth, height: window.innerHeight } },
            user
        );
    }, [user]);

    // -------- State --------
    const [documents, setDocuments] = useState([]);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [, setRoles] = useState([]);
    const [newRoles, setNewRoles] = useState([]);
    const [showModal, setShowModal] = useState(false);

    const [documentCounts, setDocumentCounts] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalDocuments, setTotalDocuments] = useState(0);
    const [sortField, setSortField] = useState("date_created");
    const [sortOrder, setSortOrder] = useState("desc");

    const [loading, setLoading] = useState(false);
    const [errorNote, setErrorNote] = useState("");

    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewContent, setPreviewContent] = useState(null);
    const [previewType, setPreviewType] = useState(null);
    const [previewError, setPreviewError] = useState(null);
    const [previewDownloadUrl, setPreviewDownloadUrl] = useState("");
    const [selectedDocTitle, setSelectedDocTitle] = useState("");

    // Smart search
    const [q, setQ] = useState("");
    const [engine, setEngine] = useState("server");
    const [elapsedMs, setElapsedMs] = useState(null);
    const searchInputRef = useRef(null);

    const setSmartSearch = (value) => {
        paramsRef.current = { ...paramsRef.current, rankFirst: true };
        setCurrentPage(1);
        setQ(value);
    };

    // Summary
    const [showMetadataModal, setShowMetadataModal] = useState(false);
    const [metadataText, setMetadataText] = useState("");
    const [selectedDetailDoc, setSelectedDetailDoc] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({
        title: "",
        description: "",
        date_created: "",
        version: "",
        addendum: "",
        author_initials: "",
        doi: "",
        submitted_by: "",
        constitution: "",
        country: "",
        orderCode: "",
        lodge: "",
        eventType: "",
        documentType: "",
        fileType: "",
    });
    const [showDescriptionToolModal, setShowDescriptionToolModal] = useState(false);
    const [descriptionToolLoading, setDescriptionToolLoading] = useState(false);
    const [descriptionToolError, setDescriptionToolError] = useState("");
    const [descriptionSuggestion, setDescriptionSuggestion] = useState(null);
    const [descriptionDraft, setDescriptionDraft] = useState("");
    const [showBatchReviewModal, setShowBatchReviewModal] = useState(false);
    const [batchReviewLoading, setBatchReviewLoading] = useState(false);
    const [batchReviewError, setBatchReviewError] = useState("");
    const [batchReviewItems, setBatchReviewItems] = useState([]);
    const [batchReviewRemaining, setBatchReviewRemaining] = useState(null);
    const [batchReviewLimit, setBatchReviewLimit] = useState(15);
    const [showPersonCompileModal, setShowPersonCompileModal] = useState(false);
    const [personCompileName, setPersonCompileName] = useState("");
    const [personCompileUseFilters, setPersonCompileUseFilters] = useState(false);
    const [personCompileLoading, setPersonCompileLoading] = useState(false);
    const [personCompileError, setPersonCompileError] = useState("");
    const [personCompileResult, setPersonCompileResult] = useState(null);
    const [personCompileProgress, setPersonCompileProgress] = useState(null);
    const [publicDestinations, setPublicDestinations] = useState([]);
    const [publicDestinationsLoading, setPublicDestinationsLoading] = useState(false);
    const [publicDestinationsSaving, setPublicDestinationsSaving] = useState(false);
    const [publicDestinationsError, setPublicDestinationsError] = useState("");
    const [publicDestinationsMessage, setPublicDestinationsMessage] = useState("");

    const [targetId, setTargetId] = useState(null);
    const location = useLocation();
    const [booted, setBooted] = useState(false);

    // Filters open/closed
    const [filtersOpen, setFiltersOpen] = useState(true);

    // Dropdown sources
    const [dropdowns, setDropdowns] = useState({
        constitutions: [],
        countries: [],
        orderCodes: [],
        lodges: [],
        eventTypes: [],
        documentTypes: [],
        fileTypes: [],
        ids: [],
        titles: [],
        uploaders: [],
        submitters: [],
    });
    const [filterCounts, setFilterCounts] = useState([]);

    // Filters
    const [filterCriteria, setFilterCriteria] = useState({
        id: "",
        title: "",
        constitution: "",
        country: "",
        orderCode: "",
        lodge: "",
        eventType: "",
        documentType: "",
        fileType: "",
        dateYear: "",
        dateMonth: "",
        dateDay: "",
        version: "",
        addendum: "",
        author_initials: "",
        doi: "",
        uploadedBy: "",
        submittedBy: "",
    });

    const smartActive = useMemo(() => q.trim().length > 0, [q]);

    // -------- Counts --------
    const fetchDocumentCounts = async (filters = {}) => {
        try {
            const response = await axios.get("/api/document-counts", { params: filters });
            setDocumentCounts(response.data.counts);
        } catch {
            // non-critical
        }
    };

    useEffect(() => {
        fetchDocumentCounts();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const docId = params.get("id");
        const query = params.get("q");

        if (docId) {
            setTargetId(docId);
            setSmartSearch("");
            setCurrentPage(1);
            setFilterCriteria((prev) => ({ ...prev, id: docId }));
            handleSearch({ id: docId }).finally(() => setBooted(true));
        } else if (query) {
            setTargetId(null);
            setSmartSearch(query);
            setCurrentPage(1);
            setFilterCriteria((prev) => ({ ...prev, id: "" }));
            handleSearch({ q: query, id: "", offset: 0 }).finally(() => setBooted(true));
        } else {
            setBooted(true);
        }
    }, [location.search]);

    useEffect(() => {
        if (!targetId || !documents.length) return;
        const row = document.querySelector(`[data-doc-id="${targetId}"]`);
        if (row) {
            row.scrollIntoView({ behavior: "smooth", block: "center" });
            row.classList.add("row-highlight");
            setTimeout(() => row.classList.remove("row-highlight"), 2000);
        }
    }, [documents, targetId]);

    // -------- Roles for current user --------
    const fetchDocumentRoles = async () => {
        try {
            const response = await axios.get("/api/document-roles", {
                params: { role: user.role },
            });
            return response.data;
        } catch {
            return [];
        }
    };

    const handleOpenModal = async (docId) => {
        try {
            const response = await axios.get(`/api/document-roles/${docId}`);
            setRoles(response.data.roles);
            setNewRoles(response.data.roles);
            setSelectedDoc(docId);
            setShowModal(true);
        } catch (error) {
            console.error("Error fetching roles:", error);
        }
    };

    const handleRoleChange = (role) => {
        setNewRoles((prev) =>
            prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
        );
    };

    const handleSaveRoles = async () => {
        try {
            await axios.post(`/api/document-roles/${selectedDoc}`, { roles: newRoles });
            alert("Roles updated successfully!");
            setShowModal(false);
            if (smartActive) debouncedRun(q);
            else handleSearch();
        } catch (error) {
            console.error("Error updating roles:", error);
        }
    };

    // -------- Dropdown data --------
    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const [
                    constitutions,
                    countries,
                    orderCodes,
                    lodges,
                    eventTypes,
                    documentTypes,
                    fileTypes,
                    ids,
                    titles,
                    uploaders,
                    submitters,
                    counts,
                ] = await Promise.all([
                    axios.get("/api/constitutions"),
                    axios.get("/api/countries"),
                    axios.get("/api/order-codes"),
                    axios.get("/api/lodges"),
                    axios.get("/api/event-types"),
                    axios.get("/api/document-types"),
                    axios.get("/api/file-types"),
                    axios.get("/api/document-ids"),
                    axios.get("/api/document-titles"),
                    axios.get("/api/uploaders"),
                    axios.get("/api/document-submitters"),
                    axios.get("/api/document-keywords"),
                ]);

                setDropdowns({
                    constitutions: constitutions.data.map((item) => ({
                        value: item.code,
                        label: item.description,
                    })),
                    countries: countries.data,
                    orderCodes: orderCodes.data,
                    lodges: lodges.data,
                    eventTypes: eventTypes.data,
                    documentTypes: documentTypes.data,
                    fileTypes: fileTypes.data,
                    ids: ids.data,
                    titles: titles.data,
                    uploaders: uploaders.data.map((u) => ({
                        value: String(u.user_id),
                        label: u.display,
                    })),
                    submitters: (submitters.data || []).map((item) => ({
                        value: item.value,
                        label: item.count == null ? item.label : `${item.label} (${item.count})`,
                    })),
                });
                setFilterCounts(counts.data || []);
            } catch (error) {
                console.error("Error fetching dropdown data:", error);
            }
        };
        fetchDropdownData();
    }, []);

    // -------- Delete --------
    const handleDeleteDocument = async (docId) => {
        const confirmed = window.confirm(
            "Are you sure you want to delete this document? This action cannot be undone."
        );
        if (!confirmed) return;
        try {
            await axios.delete(`/api/documents/${docId}`);
            alert("Document deleted successfully!");
            setSelectedDetailDoc(null);
            if (smartActive) debouncedRun(q);
            else handleSearch();
        } catch (error) {
            console.error("Error deleting document:", error);
            alert("Failed to delete the document. Please try again.");
        }
    };

    const getCodeByLabel = (items, label, labelKey = "description") => {
        if (!label) return "";
        return items.find((item) => item[labelKey] === label)?.code || "";
    };

    const openEditModal = (doc) => {
        setEditForm({
            title: doc.title || "",
            description: doc.description || "",
            date_created: doc.date_created ? convertToSAST(doc.date_created) : "",
            version: doc.version || "",
            addendum: doc.addendum || "",
            author_initials: doc.author_initials || "",
            doi: doc.doi || "",
            submitted_by: doc.submitted_by || "",
            constitution: doc.constitution_code || dropdowns.constitutions.find((item) => item.label === doc.constitution)?.value || "",
            country: doc.country_code || getCodeByLabel(dropdowns.countries, doc.country, "name"),
            orderCode: doc.order_code || getCodeByLabel(dropdowns.orderCodes, doc.order_code_description),
            lodge: doc.lodge_code || getCodeByLabel(dropdowns.lodges, doc.lodge, "name"),
            eventType: doc.event_type_code || getCodeByLabel(dropdowns.eventTypes, doc.event_type_description),
            documentType: doc.document_type_code || getCodeByLabel(dropdowns.documentTypes, doc.document_type_description),
            fileType: doc.file_type_code || getCodeByLabel(dropdowns.fileTypes, doc.file_type_description),
        });
        setSelectedDoc(doc.id);
        setShowEditModal(true);
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleEditSelectChange = (name, selectedOption) => {
        setEditForm((prev) => ({ ...prev, [name]: selectedOption ? selectedOption.value : "" }));
    };

    const handleSaveDocumentEdit = async () => {
        try {
            await axios.put(`/api/documents/${selectedDoc}`, editForm);
            setShowEditModal(false);
            if (smartActive) debouncedRun(q);
            else await handleSearch();
        } catch (error) {
            alert(error?.response?.data?.error || "Failed to update the document.");
        }
    };

    const openDescriptionTool = async (doc, force = false) => {
        setSelectedDoc(doc.id);
        setSelectedDetailDoc(doc);
        setShowDescriptionToolModal(true);
        setDescriptionToolLoading(true);
        setDescriptionToolError("");
        if (force) {
            setDescriptionSuggestion(null);
            setDescriptionDraft("");
        }

        try {
            const { data } = await axios.post(`/api/documents/${doc.id}/description-suggestion`, { force });
            setDescriptionSuggestion(data);
            setDescriptionDraft(data.suggestedDescription || "");
            setDescriptionToolError(data.error || "");
        } catch (error) {
            setDescriptionToolError(error?.response?.data?.error || "Unable to suggest a description for this document.");
        } finally {
            setDescriptionToolLoading(false);
        }
    };

    const handleApplyDescriptionSuggestion = async () => {
        if (!selectedDoc || !descriptionDraft.trim()) return;

        try {
            await axios.post(`/api/documents/${selectedDoc}/description-apply`, {
                description: descriptionDraft.trim(),
            });
            setShowDescriptionToolModal(false);
            setDescriptionSuggestion(null);
            setDescriptionDraft("");
            if (smartActive) debouncedRun(q);
            else await handleSearch();
        } catch (error) {
            setDescriptionToolError(error?.response?.data?.error || "Failed to apply the suggested description.");
        }
    };

    const openBatchDescriptionReview = async () => {
        setShowBatchReviewModal(true);
        setBatchReviewLoading(true);
        setBatchReviewError("");
        setBatchReviewItems([]);

        try {
            const { data } = await axios.post("/api/documents/description-review/batch", {
                limit: batchReviewLimit,
                includeCached: false,
            });
            setBatchReviewItems(
                (data.items || []).map((item) => ({
                    ...item,
                    draft: item.suggestedDescription || item.currentDescription || "",
                    applied: item.status === "approved",
                }))
            );
            setBatchReviewRemaining(data.remaining);
        } catch (error) {
            setBatchReviewError(error?.response?.data?.error || "Failed to prepare the description review queue.");
        } finally {
            setBatchReviewLoading(false);
        }
    };

    const updateBatchDraft = (documentId, draft) => {
        setBatchReviewItems((prev) =>
            prev.map((item) =>
                item.document?.id === documentId ? { ...item, draft } : item
            )
        );
    };

    const applyBatchDescription = async (item) => {
        const documentId = item.document?.id;
        const draft = item.draft?.trim();
        if (!documentId || !draft) return;

        try {
            await axios.post(`/api/documents/${documentId}/description-apply`, {
                description: draft,
            });
            setBatchReviewItems((prev) =>
                prev.map((row) =>
                    row.document?.id === documentId
                        ? { ...row, status: "approved", applied: true, currentDescription: draft }
                        : row
                )
            );
            if (selectedDetailDoc?.id === documentId) {
                setSelectedDetailDoc((prev) => prev ? {
                    ...prev,
                    description: draft,
                    description_review_status: "approved",
                    description_updated_by_tool: true,
                } : prev);
            }
        } catch (error) {
            setBatchReviewItems((prev) =>
                prev.map((row) =>
                    row.document?.id === documentId
                        ? { ...row, error: error?.response?.data?.error || "Failed to apply this description." }
                        : row
                )
            );
        }
    };

    // -------- Filters --------
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilterCriteria({ ...filterCriteria, [name]: value });
    };

    const handleDropdownChange = (field, value) => {
        setFilterCriteria((prev) => ({ ...prev, [field]: value }));
    };

    const handleClearFilters = () => {
        setFilterCriteria({
            id: "",
            title: "",
            constitution: "",
            country: "",
            orderCode: "",
            lodge: "",
            eventType: "",
            documentType: "",
            fileType: "",
            dateYear: "",
            dateMonth: "",
            dateDay: "",
            version: "",
            addendum: "",
            author_initials: "",
            doi: "",
            uploadedBy: "",
            submittedBy: "",
        });
        setSmartSearch("");
        setCurrentPage(1);
        setSortField("date_created");
        setSortOrder("desc");
        setMetadataText("");
        setErrorNote("");
        setElapsedMs(null);
        setEngine("server");
    };

    // -------- Pagination & sort --------
    const pageSizeOptions = [
        { value: 10, label: "10" },
        { value: 25, label: "25" },
        { value: 50, label: "50" },
        { value: 100, label: "100" },
        { value: 200, label: "200" },
        { value: 300, label: "300" },
        { value: 400, label: "400" },
        { value: 500, label: "500" },
    ];

    const runSmartSearchWithParams = (nextParams = {}) => {
        paramsRef.current = {
            ...paramsRef.current,
            ...nextParams,
        };
        debouncedRun(q);
    };

    const changePageSize = (nextPageSize) => {
        setPageSize(nextPageSize);
        setCurrentPage(1);
        if (smartActive) {
            runSmartSearchWithParams({ pageSize: nextPageSize, currentPage: 1 });
        }
    };

    const changePage = (nextPage) => {
        const maxPage = Math.max(1, Math.ceil(totalDocuments / pageSize));
        const boundedPage = Math.min(Math.max(nextPage, 1), maxPage);
        setCurrentPage(boundedPage);
        if (smartActive) {
            runSmartSearchWithParams({ currentPage: boundedPage });
        }
    };

    const sortDocs = (docs, field, order) => {
        const dir = order === "asc" ? 1 : -1;
        const sorted = [...docs].sort((a, b) => {
            let av = a[field];
            let bv = b[field];

            if (field === "date_created") {
                av = a.date_created ? new Date(a.date_created).getTime() : null;
                bv = b.date_created ? new Date(b.date_created).getTime() : null;
            } else if (typeof av === "string" && typeof bv === "string") {
                av = av.toLowerCase();
                bv = bv.toLowerCase();
            }

            if (av == null && bv == null) return 0;
            if (av == null) return 1 * dir;
            if (bv == null) return -1 * dir;

            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });

        return sorted;
    };

    const handleSort = (field) => {
        const newSortOrder = sortField === field && sortOrder === "asc" ? "desc" : "asc";
        setSortField(field);
        setSortOrder(newSortOrder);
        setCurrentPage(1);

        if (engine === "fuzzy") {
            setDocuments((prev) => sortDocs(prev, field, newSortOrder));
            return;
        }

        if (smartActive) {
            paramsRef.current = {
                ...paramsRef.current,
                currentPage: 1,
                sortField: field,
                sortOrder: newSortOrder,
                rankFirst: false,
            };
            debouncedRun(q);
            return;
        }

        handleSearch({ sortField: field, sortOrder: newSortOrder, offset: 0 });
    };

    // -------- Utilities --------
    const convertToSAST = (utcDate) => {
        const date = new Date(utcDate);
        date.setHours(date.getHours() + 2);
        return date.toISOString().split("T")[0];
    };

    const buildDownloadUrl = (docOrPath) => {
        if (docOrPath?.id) {
            return `${window.location.origin}/api/documents/${encodeURIComponent(docOrPath.id)}/download`;
        }
        const serverPath = "/opt/websites/freemasonry2/express/documents/";
        const rawPath = docOrPath || "";
        const rel = rawPath.startsWith(serverPath) ? rawPath.slice(serverPath.length) : rawPath;
        const encoded = rel.split("/").map((seg) => encodeURIComponent(seg)).join("/");
        return `${window.location.origin}/documents/${encoded}`;
    };

    // -------- Summary helpers --------
    const generateMetadata = () => {
        const meta = documents
            .map((doc) => {
                return `
Date: ${convertToSAST(doc.date_created)}
ID: ${doc.id}
Event Type: ${doc.event_type_description || ""}
Order Code: ${doc.order_code_description || ""}
Lodge: ${doc.lodge || ""}
Constitution: ${doc.constitution || ""}
Country: ${doc.country || ""}
Document Type: ${doc.document_type_description || ""}
File Type: ${doc.file_type_description || ""}
Version: ${doc.version || ""}
Addendum: ${doc.addendum || ""}
Author(s): ${doc.author_initials || ""}
DOI: ${doc.doi || ""}
Submitted By: ${doc.submitted_by || ""}
Title: ${doc.title || ""}
Description: ${doc.description || ""}

-------------------------
`.trim();
            })
            .join("\n\n");
        setMetadataText(meta);
        setShowMetadataModal(true);
    };

    const handleCopyMetadata = () => {
        navigator.clipboard
            .writeText(metadataText)
            .then(() => alert("Summary copied to clipboard!"))
            .catch((err) => console.error("Copy failed:", err));
    };

    const handleDownloadSummary = () => {
        const blob = new Blob([metadataText || ""], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `document-summary-${new Date().toISOString().slice(0, 10)}.txt`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const openDeepOcrSearch = () => {
        setPersonCompileName(q.trim());
        setPersonCompileUseFilters(false);
        setPersonCompileError("");
        setPersonCompileResult(null);
        setPersonCompileProgress(null);
        setShowPersonCompileModal(true);
    };

    const runPersonCompile = async () => {
        const name = personCompileName.trim();
        if (name.length < 3) {
            setPersonCompileError("Enter at least three characters to search for.");
            return;
        }

        setPersonCompileLoading(true);
        setPersonCompileError("");
        setPersonCompileResult(null);
        setPersonCompileProgress({
            phase: "queued",
            processed: 0,
            candidates: 0,
            scanned: 0,
            readable: 0,
            skipped: 0,
            matches: 0,
            metadataMatches: 0,
        });

        try {
            const { data } = await axios.post("/api/documents/person-compile/start", {
                name,
                filters: personCompileUseFilters ? filterCriteria : {},
                limitDocuments: "all",
                maxMatchesPerDocument: 4,
            });

            let job = data;
            while (job?.status === "queued" || job?.status === "running") {
                setPersonCompileProgress(job.progress || null);
                await new Promise((resolve) => setTimeout(resolve, 1000));
                const statusResponse = await axios.get(`/api/documents/person-compile/status/${job.jobId || job.id}`);
                job = statusResponse.data;
            }

            setPersonCompileProgress(job?.progress || null);
            if (job?.status === "complete") {
                setPersonCompileResult(job.result);
            } else {
                setPersonCompileError(job?.error || "Failed to run deep OCR search.");
            }
        } catch (error) {
            setPersonCompileError(error?.response?.data?.error || "Failed to run deep OCR search.");
        } finally {
            setPersonCompileLoading(false);
        }
    };

    const personCompilePercent = useMemo(() => {
        const total = Number(personCompileProgress?.candidates || 0);
        if (!total) return 0;
        return Math.max(0, Math.min(100, Math.round((Number(personCompileProgress?.processed || 0) / total) * 100)));
    }, [personCompileProgress]);

    const personCompileBreakdown = useMemo(() => {
        const candidateDocuments = personCompileResult?.candidateDocuments || [];
        const countStatus = (status) => candidateDocuments.filter((item) => item.status === status).length;
        return {
            considered: personCompileResult?.candidates || candidateDocuments.length || 0,
            excerpts: personCompileResult?.matches?.length || 0,
            metadataExcerpts: personCompileResult?.metadataMatches || 0,
            textExcerpts: Math.max(0, (personCompileResult?.matches?.length || 0) - (personCompileResult?.metadataMatches || 0)),
            textScanned: personCompileResult?.readable || 0,
            skipped: personCompileResult?.skipped?.length || countStatus("skipped"),
            noMatch: countStatus("readable_no_match"),
            noExtractedText: countStatus("readable_no_text"),
        };
    }, [personCompileResult]);

    const renderPersonCompileExcerpt = (excerpt = "") => {
        const term = personCompileResult?.name || personCompileName;
        const trimmedTerm = String(term || "").trim();
        if (!trimmedTerm) return excerpt;
        const escaped = trimmedTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const pieces = String(excerpt || "").split(new RegExp(`(${escaped})`, "ig"));
        return pieces.map((piece, index) => (
            piece.toLowerCase() === trimmedTerm.toLowerCase()
                ? <mark key={index}>{piece}</mark>
                : <React.Fragment key={index}>{piece}</React.Fragment>
        ));
    };

    const personCompileText = useMemo(() => {
        if (!personCompileResult) return "";
        const header = [
            `Deep OCR search: ${personCompileResult.name}`,
            `Generated: ${new Date().toISOString()}`,
            `Documents considered: ${personCompileBreakdown.considered}`,
            `Text-scanned documents: ${personCompileBreakdown.textScanned}`,
            `Excerpts: ${personCompileBreakdown.excerpts}`,
            `Document-text excerpts: ${personCompileBreakdown.textExcerpts}`,
            `Metadata excerpts: ${personCompileBreakdown.metadataExcerpts}`,
            `Unreadable/skipped documents: ${personCompileBreakdown.skipped}`,
            `Text-scanned documents with no match: ${personCompileBreakdown.noMatch}`,
            `Documents with no extractable text: ${personCompileBreakdown.noExtractedText}`,
            "",
            "MATCHED EXCERPTS",
            "",
        ].join("\n");

        const body = (personCompileResult.matches || []).map((match, index) => {
            return [
                `${index + 1}. ${match.title || "Untitled document"}`,
                `Document ID: ${match.documentId}`,
                `Lodge: ${match.lodge || ""}`,
                `Date: ${match.date ? convertToSAST(match.date) : ""}`,
                `Source: ${match.sourceLabel || "Document text"}`,
                `Type: ${[match.documentType, match.eventType, match.fileType].filter(Boolean).join(" / ")}`,
                `Excerpt: ${match.excerpt || ""}`,
            ].join("\n");
        }).join("\n\n");

        const skipped = (personCompileResult.skipped || []).length
            ? [
                "",
                "",
                "SKIPPED DOCUMENTS",
                "",
                ...(personCompileResult.skipped || []).map((item, index) => {
                    return `${index + 1}. ${item.title || "Untitled document"}\nDocument ID: ${item.id}\nReason: ${item.reason || ""}`;
                }),
            ].join("\n")
            : "";

        const candidates = (personCompileResult.candidateDocuments || []).length
            ? [
                "",
                "",
                "DOCUMENTS CONSIDERED",
                "",
                ...(personCompileResult.candidateDocuments || []).map((item, index) => {
                    return [
                        `${index + 1}. ${item.title || "Untitled document"}`,
                        `Document ID: ${item.id}`,
                        `Lodge: ${item.lodge || ""}`,
                        `Date: ${item.date ? convertToSAST(item.date) : ""}`,
                        `Type: ${[item.documentType, item.eventType, item.fileType].filter(Boolean).join(" / ")}`,
                        `Status: ${item.status || ""}`,
                        `Matches: ${item.matchCount || 0}`,
                        item.reason ? `Reason: ${item.reason}` : "",
                    ].filter(Boolean).join("\n");
                }),
            ].join("\n")
            : "";

        return `${header}${body}${skipped}${candidates}`;
    }, [personCompileResult, personCompileBreakdown]);

    const handleDownloadPersonCompile = () => {
        const blob = new Blob([personCompileText], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const safeName = (personCompileResult?.name || "search").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
        a.href = url;
        a.download = `deep-ocr-search-${safeName || "search"}-${new Date().toISOString().slice(0, 10)}.txt`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const handleDownload = (doc, downloadHref) => {
        const additionalData = {
            document: {
                id: doc.id,
                title: doc.title,
                file_type: doc.file_type_description || null,
                document_type: doc.document_type_description || null,
                event_type: doc.event_type_description || null,
                lodge: doc.lodge || null,
                order_code: doc.order_code_description || null,
                country: doc.country || null,
                constitution: doc.constitution || null,
                version: doc.version || null,
                addendum: doc.addendum || null,
                doi: doc.doi || null,
                uploaded_by: doc.uploaded_by_display || (doc.uploaded_by_user_id ? `User #${doc.uploaded_by_user_id}` : null),
                created_date_sast: convertToSAST(doc.date_created),
                download_url: downloadHref,
            },
            query_context: {
                q,
                engine,
                filters: { ...filterCriteria },
                sort: { field: sortField, order: sortOrder },
                paging: { page: currentPage, page_size: pageSize },
                total_documents: totalDocuments,
            },
            client_nonce:
                window.crypto?.randomUUID?.() ||
                `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        };

        logInteraction("document_download", additionalData, user);
    };

    useEffect(() => {
        setMetadataText("");
    }, [documents]);

    useEffect(() => {
        if (!selectedDetailDoc) return;
        const freshDoc = documents.find((doc) => doc.id === selectedDetailDoc.id);
        if (freshDoc) {
            setSelectedDetailDoc(freshDoc);
        }
    }, [documents, selectedDetailDoc]);

    const loadPublicDestinations = useCallback(async (documentId) => {
        if (!documentId || !canManageAccess) {
            setPublicDestinations([]);
            return;
        }

        setPublicDestinationsLoading(true);
        setPublicDestinationsError("");
        setPublicDestinationsMessage("");

        try {
            const response = await axios.get(`/api/admin/documents/${documentId}/public-destinations`);
            setPublicDestinations(response.data?.destinations || []);
        } catch (error) {
            setPublicDestinations([]);
            setPublicDestinationsError(error.response?.data?.error || "Could not load public review destinations.");
        } finally {
            setPublicDestinationsLoading(false);
        }
    }, [canManageAccess]);

    useEffect(() => {
        if (!selectedDetailDoc?.id || !canManageAccess) {
            setPublicDestinations([]);
            setPublicDestinationsError("");
            setPublicDestinationsMessage("");
            return;
        }

        loadPublicDestinations(selectedDetailDoc.id);
    }, [selectedDetailDoc?.id, canManageAccess, loadPublicDestinations]);

    const handlePublicDestinationToggle = async (destinationKey) => {
        if (!selectedDetailDoc?.id || publicDestinationsSaving) return;

        const nextKeys = publicDestinations
            .filter((destination) => destination.key === destinationKey ? !destination.selected : destination.selected)
            .map((destination) => destination.key);

        setPublicDestinationsSaving(true);
        setPublicDestinationsError("");
        setPublicDestinationsMessage("");

        try {
            const response = await axios.put(
                `/api/admin/documents/${selectedDetailDoc.id}/public-destinations`,
                { collectionKeys: nextKeys }
            );
            setPublicDestinations(response.data?.destinations || []);
            setPublicDestinationsMessage("Public review destinations updated.");
        } catch (error) {
            setPublicDestinationsError(error.response?.data?.error || "Could not update public review destinations.");
        } finally {
            setPublicDestinationsSaving(false);
        }
    };

    // -------- Preview --------
    const handlePreview = async (doc) => {
        setPreviewContent(null);
        setPreviewType(null);
        setPreviewError(null);
        setPreviewDownloadUrl(doc?.id ? buildDownloadUrl(doc) : (doc?.file_path ? buildDownloadUrl(doc.file_path) : ""));
        setSelectedDocTitle(doc?.title || "Document");
        setShowPreviewModal(true);

        try {
            const response = await axios.get(`/api/preview-document/${doc.id}`);
            const { preview, type, fileType, isLargeFile, pdfUrl } = response.data || {};
            const normalizedFileType = (fileType || "").replace(/^\./, "").toLowerCase();

            if (isLargeFile) {
                setPreviewError(preview || "File too large. Please download it instead.");
                return;
            }

            let inferredType = type || null;
            if (!inferredType) {
                if (typeof preview === "string" && preview.startsWith("data:image/")) inferredType = "image";
                else if (normalizedFileType === "pdf" && pdfUrl) inferredType = "pdf";
                else inferredType = "text";
            }

            setPreviewType(inferredType);
            setPreviewContent(preview || pdfUrl || null);
        } catch (error) {
            setPreviewError(error?.response?.data?.error || "Unable to generate preview for this file.");
        }
    };

    // -------- Request de-racing --------
    const fetchIdRef = useRef(0);

    // -------- Search / Fetch --------
    const handleSearch = async (override = {}) => {
        const myFetchId = ++fetchIdRef.current;
        const forcedId = override.id ?? filterCriteria.id;
        const hasIdFilter = forcedId != null && String(forcedId).trim() !== "";

        try {
            setLoading(true);
            setErrorNote("");

            let params;
            const effectiveSortField = override.sortField || sortField;
            const effectiveSortOrder = override.sortOrder || sortOrder;

            if (hasIdFilter) {
                params = {
                    id: forcedId,
                    limit: 50,
                    offset: 0,
                    sortField: effectiveSortField,
                    sortOrder: effectiveSortOrder,
                };
            } else {
                const offset = override.offset ?? (currentPage - 1) * pageSize;
                params = {
                    ...filterCriteria,
                    uploadedBy: filterCriteria.uploadedBy || undefined,
                    submittedBy: filterCriteria.submittedBy || undefined,
                    limit: pageSize,
                    offset,
                    sortField: effectiveSortField,
                    sortOrder: effectiveSortOrder,
                    ...override,
                };
            }

            const [documentsResponse, rolesResponse] = await Promise.all([
                axios.get("/api/documents", { params }),
                fetchDocumentRoles(),
            ]);

            if (myFetchId !== fetchIdRef.current) return;

            const raw = documentsResponse.data?.documents ?? [];

            let sliced;
            if (hasIdFilter) {
                const exactlyOne = raw.find((d) => String(d.id) === String(forcedId));
                sliced = exactlyOne ? [exactlyOne] : [];
                setTotalDocuments(exactlyOne ? 1 : 0);
                fetchDocumentCounts({ id: forcedId });
            } else {
                sliced = raw;
                setTotalDocuments(documentsResponse.data.total_count);
                fetchDocumentCounts(params);
            }

            const withAccess = sliced.map((doc) => {
                const accessData = rolesResponse.find((r) => r.id === doc.id);
                return {
                    ...doc,
                    hasAccess: accessData ? accessData.hasAccess : false,
                    accessRoles: accessData?.roles || [],
                };
            });

            setDocuments(withAccess);
            setEngine("server");
        } catch (error) {
            if (myFetchId !== fetchIdRef.current) return;
            setErrorNote(
                error?.response?.data?.error ||
                error?.message ||
                "Failed to fetch documents or roles."
            );
        } finally {
            if (myFetchId === fetchIdRef.current) setLoading(false);
        }
    };

    useEffect(() => {
        if (!booted || smartActive) return;
        handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, smartActive, booted]);

    useEffect(() => {
        if (!booted || smartActive) return;
        setCurrentPage(1);
        handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterCriteria, smartActive, booted]);

    // ------- Smart search helpers -------
    function debounce(fn, wait = 350) {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn(...args), wait);
        };
    }

    function parseSmartQuery(text) {
        const res = {
            lodgeName: "",
            person: "",
            dateYear: "",
            dateMonth: "",
            q: text,
        };

        const personMatch =
            text.match(/person:\s*"([^"]+)"/i) || text.match(/person:\s*([^\s]+)/i);
        if (personMatch) res.person = personMatch[1];

        const lodgeMatch =
            text.match(/lodge:\s*"([^"]+)"/i) ||
            text.match(/lodge:\s*([^\s]+)/i) ||
            text.match(/from\s+([A-Za-z0-9\- ]+)/i);
        if (lodgeMatch) res.lodgeName = lodgeMatch[1].trim();

        const monthNames = [
            "january", "february", "march", "april", "may", "june",
            "july", "august", "september", "october", "november", "december",
        ];
        const monthIdx = monthNames.findIndex((m) => text.toLowerCase().includes(m));
        if (monthIdx >= 0) res.dateMonth = String(monthIdx + 1);

        const yearMatch = text.match(/\b(20\d{2}|19\d{2})\b/);
        if (yearMatch) res.dateYear = yearMatch[1];

        return res;
    }

    const paramsRef = useRef({
        pageSize,
        currentPage,
        sortField,
        sortOrder,
        rankFirst: true,
        filterCriteria,
        documents,
    });

    useEffect(() => {
        paramsRef.current = {
            pageSize,
            currentPage,
            sortField,
            sortOrder,
            rankFirst: paramsRef.current.rankFirst ?? true,
            filterCriteria,
            documents,
        };
    }, [pageSize, currentPage, sortField, sortOrder, filterCriteria, documents]);

    const debouncedRun = useMemo(
        () =>
            debounce(async (text) => {
                const t0 = performance.now();
                const {
                    pageSize: ps,
                    currentPage: cp,
                    sortField: sf,
                    sortOrder: so,
                    rankFirst,
                    filterCriteria: fc,
                    documents: localDocs,
                } = paramsRef.current;

                const trimmed = text?.trim() || "";

                if (!trimmed) {
                    setEngine("server");
                    setElapsedMs(Math.round(performance.now() - t0));
                    return;
                }

                const parsed = parseSmartQuery(text);
                const myFetchId = ++fetchIdRef.current;

                try {
                    setLoading(true);
                    setErrorNote("");
                    const offset = (cp - 1) * ps;
                    const params = {
                        ...fc,
                        lodgeName: parsed.lodgeName || undefined,
                        author_initials: parsed.person || fc.author_initials,
                        dateYear: parsed.dateYear || fc.dateYear,
                        dateMonth: parsed.dateMonth || fc.dateMonth,
                        q: parsed.q,
                        uploadedBy: fc.uploadedBy || undefined,
                        submittedBy: fc.submittedBy || undefined,
                        limit: ps,
                        offset,
                        sortField: sf,
                        sortOrder: so,
                        rankFirst,
                    };

                    const [documentsResponse, rolesResponse] = await Promise.all([
                        axios.get("/api/documents", { params }),
                        fetchDocumentRoles(),
                    ]);

                    if (myFetchId !== fetchIdRef.current) return;

                    const documentsWithAccess = documentsResponse.data.documents.map((doc) => {
                        const accessData = rolesResponse.find((r) => r.id === doc.id);
                        return {
                            ...doc,
                            hasAccess: accessData ? accessData.hasAccess : false,
                            accessRoles: accessData?.roles || [],
                        };
                    });

                    setDocuments(documentsWithAccess);
                    setTotalDocuments(documentsResponse.data.total_count);
                    fetchDocumentCounts(params);
                    setEngine("server");
                } catch (e) {
                    if (myFetchId !== fetchIdRef.current) return;
                    const textTrim = text?.trim();
                    if (textTrim) {
                        const fuse = new Fuse(localDocs, {
                            threshold: 0.35,
                            ignoreLocation: true,
                            minMatchCharLength: 2,
                            keys: [
                                "title",
                                "description",
                                "lodge",
                                "order_code_description",
                                "event_type_description",
                                "document_type_description",
                                "country",
                                "constitution",
                                "author_initials",
                                "uploaded_by_display",
                            ],
                        });
                        const items = fuse.search(textTrim).map((r) => r.item);
                        const sorted = sortDocs(items, sf, so);
                        setDocuments(sorted);
                        setTotalDocuments(sorted.length);
                        setEngine("fuzzy");
                        setErrorNote(e?.message || "Using local fuzzy fallback.");
                    }
                } finally {
                    if (myFetchId === fetchIdRef.current) {
                        setLoading(false);
                        const t1 = performance.now();
                        setElapsedMs(Math.round(t1 - t0));
                    }
                }
            }, 350),
        []
    );

    useEffect(() => {
        if (!booted) return;
        debouncedRun(q);
    }, [q, booted, debouncedRun]);

    useEffect(() => {
        if (!booted || !smartActive) return;
        debouncedRun(q);
    }, [currentPage, pageSize, sortField, sortOrder, filterCriteria, smartActive, booted, q, debouncedRun]);

    // -------- Render --------
    const yearOptions = [];
    const currentYear = new Date().getFullYear();
    const earliestYear = 0;
    for (let y = currentYear; y >= earliestYear; y--) {
        yearOptions.push({ value: y.toString(), label: y.toString() });
    }

    const monthOptions = [
        { value: "1", label: "January" },
        { value: "2", label: "February" },
        { value: "3", label: "March" },
        { value: "4", label: "April" },
        { value: "5", label: "May" },
        { value: "6", label: "June" },
        { value: "7", label: "July" },
        { value: "8", label: "August" },
        { value: "9", label: "September" },
        { value: "10", label: "October" },
        { value: "11", label: "November" },
        { value: "12", label: "December" },
    ];

    const dayOptions = Array.from({ length: 31 }, (_, i) => {
        const val = (i + 1).toString();
        return { value: val, label: val };
    });

    const filterLabels = {
        id: "ID",
        title: "Title",
        constitution: "Constitution",
        country: "Country",
        orderCode: "Order",
        lodge: "Entity",
        eventType: "Event",
        documentType: "Document type",
        fileType: "File type",
        dateYear: "Year",
        dateMonth: "Month",
        dateDay: "Day",
        version: "Version",
        addendum: "Addendum",
        author_initials: "Author",
        doi: "DOI",
        uploadedBy: "Uploader",
        submittedBy: "Submitted by",
    };

    const getFilterDisplayValue = (key, value) => {
        if (!value) return "";
        const asString = String(value);

        if (key === "constitution") {
            return dropdowns.constitutions.find((item) => item.value === asString)?.label || asString;
        }
        if (key === "country") {
            return dropdowns.countries.find((item) => item.code === asString)?.name || asString;
        }
        if (key === "orderCode") {
            return dropdowns.orderCodes.find((item) => item.code === asString)?.description || asString;
        }
        if (key === "lodge") {
            return dropdowns.lodges.find((item) => item.code === asString)?.name || asString;
        }
        if (key === "eventType") {
            return dropdowns.eventTypes.find((item) => item.code === asString)?.description || asString;
        }
        if (key === "documentType") {
            return dropdowns.documentTypes.find((item) => item.code === asString)?.description || asString;
        }
        if (key === "fileType") {
            return dropdowns.fileTypes.find((item) => item.code === asString)?.description || asString;
        }
        if (key === "dateMonth") {
            return monthOptions.find((item) => item.value === asString)?.label || asString;
        }
        if (key === "uploadedBy") {
            return dropdowns.uploaders.find((item) => item.value === asString)?.label || asString;
        }
        if (key === "submittedBy") {
            return dropdowns.submitters.find((item) => item.value === asString)?.label || asString;
        }

        return asString;
    };

    const activeFilters = Object.entries(filterCriteria)
        .filter(([, value]) => value !== "" && value != null)
        .map(([key, value]) => ({
            key,
            label: filterLabels[key] || key,
            value: getFilterDisplayValue(key, value),
        }));

    const filterCountsByKey = useMemo(() => {
        return filterCounts.reduce((acc, item) => {
            if (!item?.filter_key || item.value == null) return acc;
            const key = item.filter_key;
            if (!acc[key]) acc[key] = {};
            acc[key][String(item.value)] = Number(item.count) || 0;
            return acc;
        }, {});
    }, [filterCounts]);

    const getFilterCount = (key, value) => filterCountsByKey[key]?.[String(value)];

    const withCount = (label, key, value) => {
        const count = getFilterCount(key, value);
        return count == null ? label : `${label} (${count})`;
    };

    const clearSingleFilter = (key) => {
        setFilterCriteria((prev) => ({ ...prev, [key]: "" }));
        setCurrentPage(1);
    };

    return (
        <div className="document-library">
            <section className="library-hero">
                <div>
                    <p className="library-kicker">Connaught Research Lodge archive</p>
                    <h2>View Documents</h2>
                    <p className="library-subtitle">
                        Search, filter, preview, and download lodge records from one focused workspace.
                    </p>
                </div>

                <div className="library-stats">
                    <span>{totalDocuments}</span>
                    <small>matching documents</small>
                    {documentCounts && <small>{documentCounts.total_count} in archive</small>}
                </div>
            </section>

            <section className="library-search-panel">
                <div className="smart-search library-search">
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={q}
                        onChange={(e) => setSmartSearch(e.target.value)}
                        placeholder={`Search title, lodge, author, description, date... Try: "installation notes" or marc leinster`}
                    />
                    {q && (
                        <button
                            className="btn-secondary"
                            onClick={() => {
                                setSmartSearch("");
                            }}
                            aria-label="Clear smart search"
                        >
                            Clear
                        </button>
                    )}
                </div>

                <div className="deep-ocr-search-row">
                    <div>
                        <strong>Deep OCR search</strong>
                        <span>Scan readable document text for the current search term and collect source excerpts.</span>
                    </div>
                    <button
                        onClick={openDeepOcrSearch}
                        className="btn-secondary"
                        title="Scan readable PDFs, Word documents, and text files for source excerpts"
                    >
                        Open Deep Search
                    </button>
                </div>

                <div className="library-status-row">
                    <div className="library-status">
                        {smartActive && (
                            <span className="search-active-note">
                                Smart search is active. Filters and sort still apply.
                            </span>
                        )}
                        {elapsedMs != null && (
                            <span className={`pill ${engine === "server" ? "pill-server" : "pill-fuzzy"}`}>
                                {engine === "server" ? "Server search" : "Local fuzzy"} · {elapsedMs} ms
                            </span>
                        )}
                        {errorNote && (
                            <span className="note-warn" role="status">
                                {errorNote}
                            </span>
                        )}
                    </div>

                    <div className="results-actions">
                        {canReviewDescriptions && (
                            <>
                                <label className="batch-limit-control" title="Number of documents to prepare in one batch">
                                    <span>Batch</span>
                                    <select
                                        value={batchReviewLimit}
                                        onChange={(event) => setBatchReviewLimit(Number(event.target.value))}
                                    >
                                        {[5, 10, 15, 25, 40].map((value) => (
                                            <option key={value} value={value}>{value}</option>
                                        ))}
                                    </select>
                                </label>
                                <button
                                    onClick={openBatchDescriptionReview}
                                    className="btn-primary"
                                    disabled={batchReviewLoading}
                                    title="Prepare description suggestions for documents that still need review"
                                >
                                    Review Descriptions
                                </button>
                            </>
                        )}
                        <button
                            onClick={generateMetadata}
                            title="Summary of current results (Cmd/Ctrl+E)"
                            className="btn-primary"
                            disabled={loading || documents.length === 0}
                        >
                            Summary
                        </button>
                        <button
                            onClick={handleCopyMetadata}
                            disabled={!metadataText}
                            className="btn-secondary"
                            title="Copy summary to clipboard"
                        >
                            Copy
                        </button>
                        <button
                            onClick={handleDownloadSummary}
                            disabled={!metadataText}
                            className="btn-secondary"
                            title="Download summary as .txt"
                        >
                            Download .txt
                        </button>
                        <button
                            onClick={handleClearFilters}
                            className="btn-tertiary"
                            title="Clear all filters"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>

                {(activeFilters.length > 0 || q) && (
                    <div className="active-filter-row" aria-label="Active filters">
                        {q && (
                            <button className="active-filter-chip" onClick={() => setSmartSearch("")}>
                                Search: {q} x
                            </button>
                        )}
                        {activeFilters.map((filter) => (
                            <button
                                key={filter.key}
                                className="active-filter-chip"
                                onClick={() => clearSingleFilter(filter.key)}
                            >
                                {filter.label}: {filter.value} x
                            </button>
                        ))}
                    </div>
                )}
            </section>

            <div className="filters-header" style={{ marginTop: 12, marginBottom: 8 }}>
                <button
                    className="btn-filters-toggle"
                    onClick={() => setFiltersOpen((open) => !open)}
                >
                    {filtersOpen ? "Hide Advanced Filters ▲" : "Show Advanced Filters ▼"}
                </button>
            </div>

            {filtersOpen && (
                <section className="advanced-filter-panel">
                    <div className="advanced-filter-panel-header">
                        <div>
                            <h3>Advanced Search</h3>
                            <p>Refine the archive by document metadata. Counts show how many records match each saved category.</p>
                        </div>
                        <button
                            onClick={handleClearFilters}
                            className="btn-secondary"
                            type="button"
                        >
                            Reset Filters
                        </button>
                    </div>
                    <div className="filter-form2">
                    {/*<div>*/}
                    {/*    <label>ID:</label>*/}
                    {/*    <Select*/}
                    {/*        menuPortalTarget={document.body}*/}
                    {/*        className="single-select"*/}
                    {/*        classNamePrefix="react-select"*/}
                    {/*        menuPlacement="auto"*/}
                    {/*        maxMenuHeight={200}*/}
                    {/*        styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}*/}
                    {/*        options={dropdowns.ids.map((id) => ({ value: id.value, label: id.label }))}*/}
                    {/*        onChange={(selectedOption) =>*/}
                    {/*            handleDropdownChange("id", selectedOption ? selectedOption.value : "")*/}
                    {/*        }*/}
                    {/*        value={*/}
                    {/*            dropdowns.ids.find((option) => option.value === filterCriteria.id)*/}
                    {/*                ? {*/}
                    {/*                    value: filterCriteria.id,*/}
                    {/*                    label: dropdowns.ids.find((id) => id.value === filterCriteria.id).label,*/}
                    {/*                }*/}
                    {/*                : null*/}
                    {/*        }*/}
                    {/*        isSearchable*/}
                    {/*        isClearable*/}
                    {/*        placeholder="Select ID"*/}
                    {/*    />*/}
                    {/*</div>*/}
                    <div>
                        <label htmlFor="filter-id">ID:</label>
                        <input
                            id="filter-id"
                            type="text"
                            name="id"
                            list="document-id-options"
                            value={filterCriteria.id}
                            onChange={handleInputChange}
                            placeholder="Type or paste ID"
                            autoComplete="off"
                        />
                        <datalist id="document-id-options">
                            {dropdowns.ids.map((id) => (
                                <option key={id.value} value={id.value}>
                                    {id.label}
                                </option>
                            ))}
                        </datalist>
                    </div>

                    <div>
                        <label>Title:</label>
                        <Select
                            options={dropdowns.titles.map((title) => ({
                                value: title.value,
                                label: title.label,
                            }))}
                            onChange={(selectedOption) =>
                                handleDropdownChange("title", selectedOption ? selectedOption.value : "")
                            }
                            value={
                                dropdowns.titles.find((option) => option.value === filterCriteria.title)
                                    ? {
                                        value: filterCriteria.title,
                                        label: dropdowns.titles.find((t) => t.value === filterCriteria.title).label,
                                    }
                                    : null
                            }
                            isSearchable
                            isClearable
                            placeholder="Select Title"
                        />
                    </div>

                    <div>
                        <label>Constitution:</label>
                        <Select
                            className="single-select"
                            classNamePrefix="react-select"
                            menuPlacement="auto"
                            maxMenuHeight={200}
                            options={dropdowns.constitutions}
                            onChange={(selectedOption) =>
                                handleDropdownChange("constitution", selectedOption ? selectedOption.value : "")
                            }
                            value={dropdowns.constitutions.find((c) => c.value === filterCriteria.constitution) || null}
                            isSearchable
                            isClearable
                            placeholder="Select Constitution"
                        />
                    </div>

                    <div>
                        <label>Country:</label>
                        <Select
                            className="single-select"
                            classNamePrefix="react-select"
                            menuPlacement="auto"
                            maxMenuHeight={200}
                            options={dropdowns.countries.map((country) => ({
                                value: country.code,
                                label: country.name,
                            }))}
                            onChange={(selectedOption) =>
                                handleDropdownChange("country", selectedOption ? selectedOption.value : "")
                            }
                            value={
                                dropdowns.countries.find((cnt) => cnt.code === filterCriteria.country)
                                    ? {
                                        value: filterCriteria.country,
                                        label: dropdowns.countries.find((cnt) => cnt.code === filterCriteria.country).name,
                                    }
                                    : null
                            }
                            isSearchable
                            isClearable
                            placeholder="Select Country"
                        />
                    </div>

                    <div>
                        <label>Order Code:</label>
                        <Select
                            className="single-select"
                            classNamePrefix="react-select"
                            menuPlacement="auto"
                            maxMenuHeight={200}
                            options={dropdowns.orderCodes.map((oc) => ({
                                value: oc.code,
                                label: oc.description,
                            }))}
                            onChange={(selectedOption) =>
                                handleDropdownChange("orderCode", selectedOption ? selectedOption.value : "")
                            }
                            value={
                                dropdowns.orderCodes.find((oc) => oc.code === filterCriteria.orderCode)
                                    ? {
                                        value: filterCriteria.orderCode,
                                        label: dropdowns.orderCodes.find((o) => o.code === filterCriteria.orderCode).description,
                                    }
                                    : null
                            }
                            isClearable
                            placeholder="Select Order Code"
                        />
                    </div>

                    <div>
                        <label>Entity (e.g. Lodge):</label>
                        <Select
                            className="single-select"
                            classNamePrefix="react-select"
                            menuPlacement="auto"
                            maxMenuHeight={200}
                            options={dropdowns.lodges.map((lodge) => ({
                                value: lodge.code,
                                label: withCount(lodge.name, "lodge", lodge.code),
                            }))}
                            onChange={(selectedOption) =>
                                handleDropdownChange("lodge", selectedOption ? selectedOption.value : "")
                            }
                            value={
                                dropdowns.lodges.find((l) => l.code === filterCriteria.lodge)
                                    ? {
                                        value: filterCriteria.lodge,
                                        label: withCount(
                                            dropdowns.lodges.find((lg) => lg.code === filterCriteria.lodge).name,
                                            "lodge",
                                            filterCriteria.lodge
                                        ),
                                    }
                                    : null
                            }
                            isClearable
                            placeholder="Select Entity"
                            menuPortalTarget={document.body}
                        />
                    </div>

                    <div>
                        <label>Event Type:</label>
                        <Select
                            className="single-select"
                            classNamePrefix="react-select"
                            menuPlacement="auto"
                            maxMenuHeight={200}
                            options={dropdowns.eventTypes.map((evt) => ({
                                value: evt.code,
                                label: withCount(evt.description, "eventType", evt.code),
                            }))}
                            onChange={(selectedOption) =>
                                handleDropdownChange("eventType", selectedOption ? selectedOption.value : "")
                            }
                            value={
                                dropdowns.eventTypes.find((e) => e.code === filterCriteria.eventType)
                                    ? {
                                        value: filterCriteria.eventType,
                                        label: withCount(
                                            dropdowns.eventTypes.find((et) => et.code === filterCriteria.eventType).description,
                                            "eventType",
                                            filterCriteria.eventType
                                        ),
                                    }
                                    : null
                            }
                            isClearable
                            placeholder="Select Event Type"
                        />
                    </div>

                    <div>
                        <label>Document Type:</label>
                        <Select
                            className="single-select"
                            classNamePrefix="react-select"
                            menuPlacement="auto"
                            maxMenuHeight={200}
                            options={dropdowns.documentTypes.map((dt) => ({
                                value: dt.code,
                                label: withCount(dt.description, "documentType", dt.code),
                            }))}
                            onChange={(selectedOption) =>
                                handleDropdownChange("documentType", selectedOption ? selectedOption.value : "")
                            }
                            value={
                                dropdowns.documentTypes.find((d) => d.code === filterCriteria.documentType)
                                    ? {
                                        value: filterCriteria.documentType,
                                        label: withCount(
                                            dropdowns.documentTypes.find((dt) => dt.code === filterCriteria.documentType).description,
                                            "documentType",
                                            filterCriteria.documentType
                                        ),
                                    }
                                    : null
                            }
                            isClearable
                            placeholder="Select Document Type"
                        />
                    </div>

                    <div>
                        <label>File Type:</label>
                        <Select
                            className="single-select"
                            classNamePrefix="react-select"
                            menuPlacement="auto"
                            maxMenuHeight={200}
                            options={dropdowns.fileTypes.map((ft) => ({
                                value: ft.code,
                                label: withCount(ft.description, "fileType", ft.code),
                            }))}
                            onChange={(selectedOption) =>
                                handleDropdownChange("fileType", selectedOption ? selectedOption.value : "")
                            }
                            value={
                                dropdowns.fileTypes.find((f) => f.code === filterCriteria.fileType)
                                    ? {
                                        value: filterCriteria.fileType,
                                        label: withCount(
                                            dropdowns.fileTypes.find((ft) => ft.code === filterCriteria.fileType).description,
                                            "fileType",
                                            filterCriteria.fileType
                                        ),
                                    }
                                    : null
                            }
                            isClearable
                            placeholder="Select File Type"
                        />
                    </div>

                    <div>
                        <label>Year:</label>
                        <Select
                            className="single-select"
                            classNamePrefix="react-select"
                            options={yearOptions.map((opt) => ({
                                ...opt,
                                label: withCount(opt.label, "dateYear", opt.value),
                            }))}
                            value={
                                yearOptions.find((opt) => opt.value === filterCriteria.dateYear)
                                    ? {
                                        value: filterCriteria.dateYear,
                                        label: withCount(filterCriteria.dateYear, "dateYear", filterCriteria.dateYear),
                                    }
                                    : null
                            }
                            onChange={(selectedOption) =>
                                handleDropdownChange("dateYear", selectedOption ? selectedOption.value : "")
                            }
                            isClearable
                            placeholder="Select Year"
                        />
                    </div>

                    <div>
                        <label>Month:</label>
                        <Select
                            className="single-select"
                            classNamePrefix="react-select"
                            options={monthOptions}
                            value={monthOptions.find((opt) => opt.value === filterCriteria.dateMonth)}
                            onChange={(selectedOption) =>
                                handleDropdownChange("dateMonth", selectedOption ? selectedOption.value : "")
                            }
                            isClearable
                            placeholder="Select Month"
                        />
                    </div>

                    <div>
                        <label>Day:</label>
                        <Select
                            className="single-select"
                            classNamePrefix="react-select"
                            options={dayOptions}
                            value={dayOptions.find((opt) => opt.value === filterCriteria.dateDay)}
                            onChange={(selectedOption) =>
                                handleDropdownChange("dateDay", selectedOption ? selectedOption.value : "")
                            }
                            isClearable
                            placeholder="Select Day"
                        />
                    </div>

                    <div>
                        <label>Version:</label>
                        <input
                            type="text"
                            name="version"
                            value={filterCriteria.version}
                            onChange={handleInputChange}
                            placeholder="Version"
                        />
                    </div>

                    <div>
                        <label>Author(s):</label>
                        <input
                            type="text"
                            name="author_initials"
                            value={filterCriteria.author_initials}
                            onChange={handleInputChange}
                            placeholder="Author(s)"
                        />
                    </div>

                    <div>
                        <label>Uploader:</label>
                        <Select
                            className="single-select"
                            classNamePrefix="react-select"
                            menuPlacement="auto"
                            maxMenuHeight={200}
                            options={dropdowns.uploaders}
                            onChange={(selected) => handleDropdownChange("uploadedBy", selected ? selected.value : "")}
                            value={dropdowns.uploaders.find((u) => u.value === filterCriteria.uploadedBy) || null}
                            isSearchable
                            isClearable
                            placeholder="Select Uploader"
                        />
                    </div>

                    <div>
                        <label>Submitted By:</label>
                        <Select
                            className="single-select"
                            classNamePrefix="react-select"
                            menuPlacement="auto"
                            maxMenuHeight={200}
                            options={dropdowns.submitters}
                            onChange={(selected) => handleDropdownChange("submittedBy", selected ? selected.value : "")}
                            value={dropdowns.submitters.find((s) => s.value === filterCriteria.submittedBy) || null}
                            isSearchable
                            isClearable
                            placeholder="Select Submitter"
                        />
                    </div>
                    </div>
                </section>
            )}

            <div className="pagination-controls">
                <label>Page Size: </label>
                <Select
                    options={pageSizeOptions}
                    value={pageSizeOptions.find((opt) => opt.value === pageSize)}
                    onChange={(selectedOption) => {
                        changePageSize(selectedOption.value);
                    }}
                    classNamePrefix="react-select"
                    isSearchable={false}
                    menuPlacement="auto"
                />
                <button
                    className="btn-secondary"
                    disabled={currentPage === 1}
                    onClick={() => changePage(currentPage - 1)}
                >
                    Previous
                </button>
                <span>
                    Page {currentPage} of {Math.max(1, Math.ceil(totalDocuments / pageSize))}
                </span>
                <button
                    className="btn-secondary"
                    disabled={currentPage >= Math.ceil(totalDocuments / pageSize)}
                    onClick={() => changePage(currentPage + 1)}
                >
                    Next
                </button>
            </div>

            <div className="document-list library-results-shell">
                <div className="library-results-header">
                    <h3>Documents</h3>
                    <div className="sort-controls">
                        <button
                            className={`sort-chip ${sortField === "date_created" ? "active" : ""}`}
                            onClick={() => handleSort("date_created")}
                        >
                            Date {sortField === "date_created" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                        </button>
                        <button
                            className={`sort-chip ${sortField === "title" ? "active" : ""}`}
                            onClick={() => handleSort("title")}
                        >
                            Title {sortField === "title" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                        </button>
                    </div>
                </div>

                <div className="library-workspace">
                    <div className="results-list-panel">
                    {loading && (
                        <div className="loading-overlay">
                            <div className="spinner" aria-hidden="true" />
                            <span>Loading…</span>
                        </div>
                    )}

                    {documents.length > 0 ? (
                        <div className="document-result-list">
                            {documents.map((doc) => {
                                const downloadHref = buildDownloadUrl(doc);
                                return (
                                    <article
                                        key={doc.id}
                                        data-doc-id={doc.id}
                                        className={`document-result-card ${!doc.hasAccess ? "no-access" : ""} ${selectedDetailDoc?.id === doc.id ? "selected" : ""}`}
                                        onClick={() => setSelectedDetailDoc(doc)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === "Enter" && setSelectedDetailDoc(doc)}
                                    >
                                        <div className="doc-result-main">
                                            <div className="doc-result-date">
                                                <span>{convertToSAST(doc.date_created)}</span>
                                                <small>ID {doc.id}</small>
                                            </div>
                                            <div className="doc-result-content">
                                                <div className="doc-result-title-row">
                                                    <h4>{doc.title || "Untitled document"}</h4>
                                                    <span className="doc-badge">{doc.file_type_description || "File"}</span>
                                                </div>
                                                <p>{doc.description || "No description has been captured yet."}</p>
                                                <div className="doc-meta-line">
                                                    <span>{doc.lodge || "Unknown lodge"}</span>
                                                    <span>{doc.document_type_description || "Document"}</span>
                                                    <span>{doc.event_type_description || "Event not set"}</span>
                                                    {doc.author_initials && <span>Author: {doc.author_initials}</span>}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="doc-result-actions">
                                            <button
                                                className="preview-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePreview(doc);
                                                }}
                                            >
                                                Preview
                                            </button>
                                            {doc.hasAccess ? (
                                                <a
                                                    className="btn-secondary document-download-link"
                                                    href={downloadHref}
                                                    download
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDownload(doc, downloadHref);
                                                    }}
                                                >
                                                    Download
                                                </a>
                                            ) : (
                                                <span className="no-access-label">No Access</span>
                                            )}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    ) : (
                        !loading && (
                            <div className="empty-state">
                                <h4>No documents found</h4>
                                <p>Try broadening your search or clearing filters.</p>
                                <div style={{ display: "flex", gap: 8 }}>
                                    <button className="btn-secondary" onClick={() => setSmartSearch("")}>Clear Smart Search</button>
                                    <button className="btn-tertiary" onClick={handleClearFilters}>Clear All Filters</button>
                                </div>
                                <div className="tips">
                                    <strong>Tips:</strong>
                                    <ul>
                                        <li>Combine words: <em>photos emerald</em></li>
                                        <li>Use quotes for phrases: <em>"installation notes"</em></li>
                                        <li>Search by lodge: <em>Connaught</em> or <em>Pretoria Leinster</em></li>
                                        <li>Search by uploader or submitter: <em>marc pienaar</em></li>
                                        <li>Typos are okay: <em>shawn</em> will still find <em>Shaun</em></li>
                                    </ul>
                                </div>
                            </div>
                        )
                    )}
                    </div>

                    <aside className={`details-panel ${selectedDetailDoc ? "open" : ""}`}>
                        {selectedDetailDoc ? (
                            <>
                                <div className="details-panel-header">
                                    <div>
                                        <span className="doc-badge">{selectedDetailDoc.file_type_description || "File"}</span>
                                        <h3>{selectedDetailDoc.title || "Untitled document"}</h3>
                                    </div>
                                    <button className="details-close" onClick={() => setSelectedDetailDoc(null)}>
                                        ×
                                    </button>
                                </div>

                                <div className="details-actions">
                                    <button className="preview-button" onClick={() => handlePreview(selectedDetailDoc)}>
                                        Preview
                                    </button>
                                    {selectedDetailDoc.hasAccess ? (
                                        <a
                                            className="btn-primary"
                                            href={buildDownloadUrl(selectedDetailDoc)}
                                            download
                                            onClick={() => handleDownload(selectedDetailDoc, buildDownloadUrl(selectedDetailDoc))}
                                        >
                                            Download
                                        </a>
                                    ) : (
                                        <span className="no-access-label">No Access</span>
                                    )}
                                </div>

                                <dl className="details-grid">
                                    <div><dt>ID</dt><dd>{selectedDetailDoc.id}</dd></div>
                                    <div><dt>Date</dt><dd>{convertToSAST(selectedDetailDoc.date_created)}</dd></div>
                                    <div><dt>Lodge</dt><dd>{selectedDetailDoc.lodge || ""}</dd></div>
                                    <div><dt>Document Type</dt><dd>{selectedDetailDoc.document_type_description || ""}</dd></div>
                                    <div><dt>Event Type</dt><dd>{selectedDetailDoc.event_type_description || ""}</dd></div>
                                    <div><dt>Constitution</dt><dd>{selectedDetailDoc.constitution || ""}</dd></div>
                                    <div><dt>Country</dt><dd>{selectedDetailDoc.country || ""}</dd></div>
                                    <div><dt>Order Code</dt><dd>{selectedDetailDoc.order_code_description || ""}</dd></div>
                                    <div><dt>Version</dt><dd>{selectedDetailDoc.version || ""}</dd></div>
                                    <div><dt>Addendum</dt><dd>{selectedDetailDoc.addendum || ""}</dd></div>
                                    <div><dt>Author(s)</dt><dd>{selectedDetailDoc.author_initials || ""}</dd></div>
                                    <div><dt>DOI</dt><dd>{selectedDetailDoc.doi || ""}</dd></div>
                                    <div><dt>Uploaded By</dt><dd>{selectedDetailDoc.uploaded_by_display || ""}</dd></div>
                                    <div><dt>Submitted By</dt><dd>{selectedDetailDoc.submitted_by || ""}</dd></div>
                                    <div>
                                        <dt>Description Review</dt>
                                        <dd>
                                            <span className={`description-status status-${selectedDetailDoc.description_review_status || "unchecked"}`}>
                                                {selectedDetailDoc.description_review_status || "unchecked"}
                                            </span>
                                        </dd>
                                    </div>
                                </dl>

                                <div className="details-description">
                                    <h4>Description</h4>
                                    <p>{selectedDetailDoc.description || "No description has been captured yet."}</p>
                                </div>

                                <div className="details-access">
                                    <h4>Access</h4>
                                    {selectedDetailDoc.accessRoles?.length ? (
                                        <div className="access-chip-row">
                                            {selectedDetailDoc.accessRoles.map((role) => (
                                                <span className="access-chip" key={role}>{role}</span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p>No roles have been assigned.</p>
                                    )}
                                </div>

                                {canManageAccess && (
                                    <div className="details-public-routing">
                                        <div className="details-public-routing-header">
                                            <h4>Public review destinations</h4>
                                            {(publicDestinationsLoading || publicDestinationsSaving) && (
                                                <span>{publicDestinationsSaving ? "Saving..." : "Loading..."}</span>
                                            )}
                                        </div>
                                        <p>
                                            Tick a section to send this document to that public page's Needs Publishing queue. It stays hidden
                                            from visitors until an archivist publishes it. Charity items appear first as archive source evidence;
                                            structured charity records and charts still come from linked charity activity data.
                                        </p>
                                        {publicDestinationsError && (
                                            <div className="public-routing-alert error">{publicDestinationsError}</div>
                                        )}
                                        {publicDestinationsMessage && (
                                            <div className="public-routing-alert success">{publicDestinationsMessage}</div>
                                        )}
                                        {publicDestinations.length ? (
                                            <div className="public-destination-list">
                                                {publicDestinations.map((destination) => (
                                                    <label
                                                        className={`public-destination-option ${destination.selected ? "selected" : ""}`}
                                                        key={destination.key}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={Boolean(destination.selected)}
                                                            disabled={publicDestinationsLoading || publicDestinationsSaving}
                                                            onChange={() => handlePublicDestinationToggle(destination.key)}
                                                        />
                                                        <span>
                                                            <strong>{destination.label}</strong>
                                                            <small>
                                                                {destination.published
                                                                    ? "Published in this public section"
                                                                    : destination.selected
                                                                        ? "Queued for review, not public yet"
                                                                        : "Not selected"}
                                                                {destination.description ? ` · ${destination.description}` : ""}
                                                            </small>
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        ) : !publicDestinationsLoading && (
                                            <div className="public-routing-alert">No public destination options loaded.</div>
                                        )}
                                    </div>
                                )}

                                {(canManageAccess || canEdit || canDelete) && (
                                    <div className="details-admin-actions">
                                        {canEdit && (
                                            <button className="btn-secondary" onClick={() => openEditModal(selectedDetailDoc)}>
                                                Edit
                                            </button>
                                        )}
                                        {canReviewDescriptions && (
                                            <button className="btn-secondary" onClick={() => openDescriptionTool(selectedDetailDoc)}>
                                                Suggest Description
                                            </button>
                                        )}
                                        {canManageAccess && (
                                            <button className="btn-secondary" onClick={() => handleOpenModal(selectedDetailDoc.id)}>
                                                Change Access
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button className="btn-danger" onClick={() => handleDeleteDocument(selectedDetailDoc.id)}>
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="details-empty">
                                <h3>Select a document</h3>
                                <p>Choose a result to inspect metadata, preview the file, or download it.</p>
                            </div>
                        )}
                    </aside>
                </div>

                    {showModal && (
                        <div className="modal">
                            <div className="modal-content">
                                <h3>Change Access for Document {selectedDoc}</h3>
                                <ul>
                                    {["Admin", "Archivist", "Member", "Researcher", "Secretary"].map((role) => (
                                        <li key={role}>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={newRoles.includes(role)}
                                                    onChange={() => handleRoleChange(role)}
                                                />
                                                {role}
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                                    <button onClick={handleSaveRoles} className="btn-primary">Save</button>
                                    <button onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showDescriptionToolModal && (
                        <div className="modal">
                            <div className="modal-content description-tool-modal">
                                <div className="description-tool-header">
                                    <div>
                                        <h3>Description Review</h3>
                                        <p>{selectedDetailDoc?.title || "Selected document"}</p>
                                    </div>
                                    <span className={`description-status status-${descriptionSuggestion?.status || "checking"}`}>
                                        {descriptionToolLoading ? "checking" : (descriptionSuggestion?.status || "unchecked")}
                                    </span>
                                </div>

                                {descriptionToolLoading ? (
                                    <div className="description-loading">Reading the document and preparing a suggested description...</div>
                                ) : (
                                    <>
                                        {descriptionToolError && (
                                            <div className="description-tool-alert">{descriptionToolError}</div>
                                        )}

                                        <div className="description-review-grid">
                                            <section>
                                                <h4>Current Description</h4>
                                                <div className="description-text-box">
                                                    {descriptionSuggestion?.currentDescription || selectedDetailDoc?.description || "No description has been captured yet."}
                                                </div>
                                            </section>
                                            <section>
                                                <h4>Suggested Description</h4>
                                                <textarea
                                                    className="description-draft"
                                                    value={descriptionDraft}
                                                    onChange={(event) => setDescriptionDraft(event.target.value)}
                                                    placeholder="No automatic suggestion is available yet."
                                                    rows={8}
                                                />
                                            </section>
                                        </div>

                                        {descriptionSuggestion?.extractedPreview ? (
                                            <section className="description-extract-preview">
                                                <h4>Extracted Text Preview</h4>
                                                <pre>{descriptionSuggestion.extractedPreview}</pre>
                                            </section>
                                        ) : null}
                                    </>
                                )}

                                <div className="description-tool-actions">
                                    <button className="preview-button" onClick={() => selectedDetailDoc && handlePreview(selectedDetailDoc)}>
                                        View File
                                    </button>
                                    <button
                                        className="btn-secondary"
                                        disabled={descriptionToolLoading || !selectedDetailDoc}
                                        onClick={() => selectedDetailDoc && openDescriptionTool(selectedDetailDoc, true)}
                                    >
                                        Try Again
                                    </button>
                                    <button
                                        className="btn-primary"
                                        disabled={descriptionToolLoading || !descriptionDraft.trim()}
                                        onClick={handleApplyDescriptionSuggestion}
                                    >
                                        Apply Description
                                    </button>
                                    <button className="btn-secondary" onClick={() => setShowDescriptionToolModal(false)}>
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showBatchReviewModal && (
                        <div className="modal">
                            <div className="modal-content batch-review-modal">
                                <div className="description-tool-header">
                                    <div>
                                        <h3>Description Review Queue</h3>
                                        <p>
                                            Prepared suggestions skip documents already approved, failed, or marked no extract.
                                            {batchReviewRemaining != null ? ` ${batchReviewRemaining} still need first review after this batch.` : ""}
                                        </p>
                                    </div>
                                    <button className="details-close" onClick={() => setShowBatchReviewModal(false)}>
                                        ×
                                    </button>
                                </div>

                                {batchReviewLoading ? (
                                    <div className="description-loading">Preparing the next batch of description suggestions...</div>
                                ) : (
                                    <>
                                        {batchReviewError && <div className="description-tool-alert">{batchReviewError}</div>}
                                        {!batchReviewItems.length && !batchReviewError ? (
                                            <div className="description-loading">No documents are waiting for description review.</div>
                                        ) : (
                                            <div className="batch-review-list">
                                                {batchReviewItems.map((item) => {
                                                    const doc = item.document || {};
                                                    const downloadHref = doc.id ? buildDownloadUrl(doc) : (doc.file_path ? buildDownloadUrl(doc.file_path) : "");
                                                    return (
                                                        <article className="batch-review-item" key={doc.id}>
                                                            <div className="batch-review-item-header">
                                                                <div>
                                                                    <span className="doc-badge">{doc.file_type_description || "File"}</span>
                                                                    <h4>{doc.title || `Document ${doc.id}`}</h4>
                                                                    <p>
                                                                        {[doc.lodge, doc.document_type_description, doc.event_type_description]
                                                                            .filter(Boolean)
                                                                            .join(" · ")}
                                                                    </p>
                                                                </div>
                                                                <span className={`description-status status-${item.status || "unchecked"}`}>
                                                                    {item.applied ? "approved" : (item.status || "unchecked")}
                                                                </span>
                                                            </div>

                                                            {item.error && <div className="description-tool-alert">{item.error}</div>}

                                                            <div className="batch-review-columns">
                                                                <section>
                                                                    <h5>Current</h5>
                                                                    <div className="description-text-box">
                                                                        {item.currentDescription || "No description has been captured yet."}
                                                                    </div>
                                                                </section>
                                                                <section>
                                                                    <h5>Suggestion</h5>
                                                                    <textarea
                                                                        className="description-draft"
                                                                        value={item.draft || ""}
                                                                        onChange={(event) => updateBatchDraft(doc.id, event.target.value)}
                                                                        rows={6}
                                                                        disabled={item.applied}
                                                                    />
                                                                </section>
                                                            </div>

                                                            <div className="batch-review-actions">
                                                                <button className="preview-button" onClick={() => handlePreview(doc)}>
                                                                    View File
                                                                </button>
                                                                {downloadHref && (
                                                                    <a
                                                                        className="btn-secondary"
                                                                        href={downloadHref}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        download
                                                                    >
                                                                        Download
                                                                    </a>
                                                                )}
                                                                {canReviewDescriptions && (
                                                                    <button className="btn-secondary" onClick={() => openEditModal({
                                                                        ...doc,
                                                                        description: item.currentDescription || doc.description || "",
                                                                    })}>
                                                                        Edit Metadata
                                                                    </button>
                                                                )}
                                                                <button
                                                                    className="btn-primary"
                                                                    disabled={item.applied || !item.draft?.trim()}
                                                                    onClick={() => applyBatchDescription(item)}
                                                                >
                                                                    {item.applied ? "Applied" : "Apply"}
                                                                </button>
                                                            </div>
                                                        </article>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </>
                                )}

                                <div className="description-tool-actions">
                                    <button
                                        className="btn-secondary"
                                        disabled={batchReviewLoading}
                                        onClick={openBatchDescriptionReview}
                                    >
                                        Load Next Batch
                                    </button>
                                    <button
                                        className="btn-secondary"
                                        onClick={() => {
                                            setShowBatchReviewModal(false);
                                            if (smartActive) debouncedRun(q);
                                            else handleSearch();
                                        }}
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showPersonCompileModal && (
                        <div className="modal">
                            <div className="modal-content person-compile-modal">
                                <div className="description-tool-header">
                                    <div>
                                        <h3>Deep OCR Search</h3>
                                        <p>
                                            Search readable PDFs, Word documents, and text files for any term, then collect source
                                            excerpts for research notes.
                                        </p>
                                    </div>
                                    <button className="details-close" onClick={() => setShowPersonCompileModal(false)}>
                                        ×
                                    </button>
                                </div>

                                <div className="person-compile-controls">
                                    <label>
                                        Search term
                                        <input
                                            value={personCompileName}
                                            onChange={(event) => setPersonCompileName(event.target.value)}
                                            placeholder="e.g. Shaun Stearn, charity, installation"
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") runPersonCompile();
                                            }}
                                        />
                                    </label>
                                    <label className="person-compile-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={personCompileUseFilters}
                                            onChange={(event) => setPersonCompileUseFilters(event.target.checked)}
                                        />
                                        Restrict to the current filters
                                    </label>
                                    <button
                                        className="btn-primary"
                                        onClick={runPersonCompile}
                                        disabled={personCompileLoading || personCompileName.trim().length < 3}
                                    >
                                        {personCompileLoading ? "Searching..." : "Search documents"}
                                    </button>
                                </div>

                                {personCompileError && <div className="description-tool-alert">{personCompileError}</div>}

                                {personCompileLoading && (
                                    <div className="person-compile-progress-card">
                                        <div className="person-compile-progress-header">
                                            <strong>
                                                {personCompileProgress?.candidates
                                                    ? `Scanning ${personCompileProgress.processed || 0} of ${personCompileProgress.candidates} documents`
                                                    : "Building candidate list"}
                                            </strong>
                                            <span>{personCompilePercent}%</span>
                                        </div>
                                        <div className="person-compile-progress-track" aria-label="Person compile progress">
                                            <div style={{ width: `${personCompilePercent}%` }} />
                                        </div>
                                        <div className="person-compile-summary">
                                            <span><strong>{personCompileProgress?.matches || 0}</strong> excerpts found</span>
                                            <span><strong>{personCompileProgress?.metadataMatches || 0}</strong> metadata excerpts</span>
                                            <span><strong>{personCompileProgress?.readable || 0}</strong> readable</span>
                                            <span><strong>{personCompileProgress?.skipped || 0}</strong> skipped</span>
                                        </div>
                                        <p>
                                            Scanning all matching documents. Scanned images and very large image-only files are skipped until OCR is added.
                                        </p>
                                    </div>
                                )}

                                {personCompileResult && !personCompileLoading && (
                                    <>
                                        <div className="person-compile-summary">
                                            <span><strong>{personCompileBreakdown.considered}</strong> documents considered</span>
                                            <span><strong>{personCompileBreakdown.textScanned}</strong> text-scanned</span>
                                            <span><strong>{personCompileBreakdown.excerpts}</strong> excerpts</span>
                                            <span><strong>{personCompileBreakdown.textExcerpts}</strong> document-text excerpts</span>
                                            <span><strong>{personCompileBreakdown.metadataExcerpts}</strong> metadata excerpts</span>
                                            <span><strong>{personCompileBreakdown.skipped}</strong> unreadable/skipped</span>
                                            <span><strong>{personCompileBreakdown.noMatch}</strong> text-scanned, no match</span>
                                            <span><strong>{personCompileBreakdown.noExtractedText}</strong> no extractable text</span>
                                            {personCompileResult.truncated && <span>Limited to first {personCompileResult.limits?.limitDocuments} candidates</span>}
                                        </div>

                                        {(personCompileResult.matches || []).length ? (
                                            <div className="person-compile-results">
                                                {personCompileResult.matches.map((match, index) => {
                                                    const downloadHref = `${window.location.origin}${match.downloadUrl}`;
                                                    return (
                                                        <article className="person-compile-hit" key={`${match.documentId}-${match.passageNumber}-${index}`}>
                                                            <div className="person-compile-hit-header">
                                                                <div>
                                                                    <span className="doc-badge">{match.documentType || "Document"}</span>
                                                                    <span className="doc-badge">{match.sourceLabel || "Document text"}</span>
                                                                    <h4>{match.title || "Untitled document"}</h4>
                                                                    <p>
                                                                        {[match.lodge, match.date ? convertToSAST(match.date) : "", match.eventType]
                                                                            .filter(Boolean)
                                                                            .join(" · ")}
                                                                    </p>
                                                                </div>
                                                                <span className="doc-badge">ID {match.documentId}</span>
                                                            </div>
                                                            <blockquote>{renderPersonCompileExcerpt(match.excerpt)}</blockquote>
                                                            <div className="person-compile-actions">
                                                                <button
                                                                    className="preview-button"
                                                                    onClick={() => handlePreview({
                                                                        id: match.documentId,
                                                                        title: match.title,
                                                                    })}
                                                                >
                                                                    Review Source
                                                                </button>
                                                                <a className="btn-secondary" href={downloadHref} target="_blank" rel="noreferrer" download>
                                                                    Download Source
                                                                </a>
                                                            </div>
                                                        </article>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="description-loading">
                                                No readable passages contained that name. Try initials, alternate spelling, or a surname only.
                                            </div>
                                        )}

                                        <div className="description-tool-actions">
                                            <button
                                                className="btn-secondary"
                                                onClick={handleDownloadPersonCompile}
                                                disabled={!personCompileText}
                                            >
                                                Download Compile .txt
                                            </button>
                                            <button className="btn-secondary" onClick={() => setShowPersonCompileModal(false)}>
                                                Done
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {showEditModal && (
                        <div className="modal">
                            <div className="modal-content document-edit-modal">
                                <h3>Edit Document</h3>
                                <label>
                                    Title
                                    <input name="title" value={editForm.title} onChange={handleEditFormChange} />
                                </label>
                                <label>
                                    Description
                                    <textarea name="description" value={editForm.description} onChange={handleEditFormChange} rows={5} />
                                </label>
                                <div className="edit-field-grid">
                                    <label>
                                        Constitution
                                        <Select
                                            className="single-select"
                                            classNamePrefix="react-select"
                                            options={dropdowns.constitutions}
                                            value={dropdowns.constitutions.find((item) => item.value === editForm.constitution) || null}
                                            onChange={(selected) => handleEditSelectChange("constitution", selected)}
                                            isSearchable
                                            isClearable
                                            placeholder="Select Constitution"
                                        />
                                    </label>
                                    <label>
                                        Country
                                        <Select
                                            className="single-select"
                                            classNamePrefix="react-select"
                                            options={dropdowns.countries.map((country) => ({
                                                value: country.code,
                                                label: country.name,
                                            }))}
                                            value={
                                                dropdowns.countries.find((country) => country.code === editForm.country)
                                                    ? {
                                                        value: editForm.country,
                                                        label: dropdowns.countries.find((country) => country.code === editForm.country).name,
                                                    }
                                                    : null
                                            }
                                            onChange={(selected) => handleEditSelectChange("country", selected)}
                                            isSearchable
                                            isClearable
                                            placeholder="Select Country"
                                        />
                                    </label>
                                    <label>
                                        Order Code
                                        <Select
                                            className="single-select"
                                            classNamePrefix="react-select"
                                            options={dropdowns.orderCodes.map((orderCode) => ({
                                                value: orderCode.code,
                                                label: orderCode.description,
                                            }))}
                                            value={
                                                dropdowns.orderCodes.find((orderCode) => orderCode.code === editForm.orderCode)
                                                    ? {
                                                        value: editForm.orderCode,
                                                        label: dropdowns.orderCodes.find((orderCode) => orderCode.code === editForm.orderCode).description,
                                                    }
                                                    : null
                                            }
                                            onChange={(selected) => handleEditSelectChange("orderCode", selected)}
                                            isSearchable
                                            isClearable
                                            placeholder="Select Order Code"
                                        />
                                    </label>
                                    <label>
                                        Entity
                                        <Select
                                            className="single-select"
                                            classNamePrefix="react-select"
                                            options={dropdowns.lodges.map((lodge) => ({
                                                value: lodge.code,
                                                label: lodge.name,
                                            }))}
                                            value={
                                                dropdowns.lodges.find((lodge) => lodge.code === editForm.lodge)
                                                    ? {
                                                        value: editForm.lodge,
                                                        label: dropdowns.lodges.find((lodge) => lodge.code === editForm.lodge).name,
                                                    }
                                                    : null
                                            }
                                            onChange={(selected) => handleEditSelectChange("lodge", selected)}
                                            isSearchable
                                            isClearable
                                            placeholder="Select Entity"
                                        />
                                    </label>
                                    <label>
                                        Event Type
                                        <Select
                                            className="single-select"
                                            classNamePrefix="react-select"
                                            options={dropdowns.eventTypes.map((eventType) => ({
                                                value: eventType.code,
                                                label: eventType.description,
                                            }))}
                                            value={
                                                dropdowns.eventTypes.find((eventType) => eventType.code === editForm.eventType)
                                                    ? {
                                                        value: editForm.eventType,
                                                        label: dropdowns.eventTypes.find((eventType) => eventType.code === editForm.eventType).description,
                                                    }
                                                    : null
                                            }
                                            onChange={(selected) => handleEditSelectChange("eventType", selected)}
                                            isSearchable
                                            isClearable
                                            placeholder="Select Event Type"
                                        />
                                    </label>
                                    <label>
                                        Document Type
                                        <Select
                                            className="single-select"
                                            classNamePrefix="react-select"
                                            options={dropdowns.documentTypes.map((documentType) => ({
                                                value: documentType.code,
                                                label: documentType.description,
                                            }))}
                                            value={
                                                dropdowns.documentTypes.find((documentType) => documentType.code === editForm.documentType)
                                                    ? {
                                                        value: editForm.documentType,
                                                        label: dropdowns.documentTypes.find((documentType) => documentType.code === editForm.documentType).description,
                                                    }
                                                    : null
                                            }
                                            onChange={(selected) => handleEditSelectChange("documentType", selected)}
                                            isSearchable
                                            isClearable
                                            placeholder="Select Document Type"
                                        />
                                    </label>
                                    <label>
                                        File Type
                                        <Select
                                            className="single-select"
                                            classNamePrefix="react-select"
                                            options={dropdowns.fileTypes.map((fileType) => ({
                                                value: fileType.code,
                                                label: fileType.description,
                                            }))}
                                            value={
                                                dropdowns.fileTypes.find((fileType) => fileType.code === editForm.fileType)
                                                    ? {
                                                        value: editForm.fileType,
                                                        label: dropdowns.fileTypes.find((fileType) => fileType.code === editForm.fileType).description,
                                                    }
                                                    : null
                                            }
                                            onChange={(selected) => handleEditSelectChange("fileType", selected)}
                                            isSearchable
                                            isClearable
                                            placeholder="Select File Type"
                                        />
                                    </label>
                                </div>
                                <div className="edit-field-grid">
                                    <label>
                                        Date
                                        <input type="date" name="date_created" value={editForm.date_created} onChange={handleEditFormChange} />
                                    </label>
                                    <label>
                                        Version
                                        <input name="version" value={editForm.version} onChange={handleEditFormChange} />
                                    </label>
                                    <label>
                                        Addendum
                                        <input name="addendum" value={editForm.addendum} onChange={handleEditFormChange} />
                                    </label>
                                    <label>
                                        Author(s)
                                        <input name="author_initials" value={editForm.author_initials} onChange={handleEditFormChange} />
                                    </label>
                                </div>
                                <label>
                                    DOI
                                    <input name="doi" value={editForm.doi} onChange={handleEditFormChange} />
                                </label>
                                <label>
                                    Submitted By
                                    <input name="submitted_by" value={editForm.submitted_by} onChange={handleEditFormChange} />
                                </label>
                                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                                    <button onClick={handleSaveDocumentEdit} className="btn-primary">Save</button>
                                    <button onClick={() => setShowEditModal(false)} className="btn-secondary">Cancel</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showPreviewModal && (
                        <PreviewModal
                            title={selectedDocTitle}
                            previewContent={previewContent}
                            previewType={previewType}
                            previewError={previewError}
                            downloadUrl={previewDownloadUrl}
                            onClose={() => setShowPreviewModal(false)}
                        />
                    )}

                    {showMetadataModal && (
                        <MetadataModal
                            metadataText={metadataText}
                            onClose={() => setShowMetadataModal(false)}
                            onCopy={handleCopyMetadata}
                        />
                    )}
            </div>
        </div>
    );
}

export default DocumentViewer;
